import type { Json } from '@fundline/db/supabase';
import {
  type ComplianceForm,
  readComplianceSettings,
  toComplianceForm,
  toStoredCompliance,
} from '@fundline/shared/schemas/workspace-settings';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../lib/queryKeys.ts';
import { supabase } from '../../lib/supabase.ts';

type JsonObject = { [key: string]: Json | undefined };

export interface ComplianceSettingsData {
  workspaceName: string;
  form: ComplianceForm;
  /** Everything else in settings, kept as is when saving. */
  rawSettings: JsonObject;
}

function asObject(value: Json): JsonObject {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

const settingsKey = (workspaceId: string) =>
  [...queryKeys.workspace(workspaceId), 'compliance'] as const;

export function useComplianceSettingsQuery(workspaceId: string) {
  return useQuery({
    queryKey: settingsKey(workspaceId),
    queryFn: async (): Promise<ComplianceSettingsData> => {
      const { data, error } = await supabase()
        .from('workspaces')
        .select('name, settings')
        .eq('id', workspaceId)
        .single();
      if (error) throw new Error(`Couldn't load the settings: ${error.message}`);
      return {
        workspaceName: data.name,
        form: toComplianceForm(readComplianceSettings(data.settings)),
        rawSettings: asObject(data.settings),
      };
    },
  });
}

/** Saves the compliance section only (admins: column grant on settings, RLS on the row). */
export function useSaveComplianceSettings(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { form: ComplianceForm; rawSettings: JsonObject }) => {
      const settings: JsonObject = {
        ...input.rawSettings,
        compliance: toStoredCompliance(input.form),
      };
      const { error } = await supabase()
        .from('workspaces')
        .update({ settings })
        .eq('id', workspaceId);
      if (error) throw new Error(`Couldn't save: ${error.message}`);
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.workspace(workspaceId) }),
        // The compose box's footer preview reads the same setting.
        queryClient.invalidateQueries({ queryKey: queryKeys.inbox(workspaceId) }),
      ]);
    },
  });
}
